"use client";

import {
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";
import dynamic from "next/dynamic";
import { NextImage, Text } from "@sitecore-content-sdk/nextjs";
import type { EmblaOptionsType } from "embla-carousel";
import {
  HeroCarouselProps,
  HeroCarouselSlide,
} from "components/hero-carousel/hero-carousel.props";
import Button from "../../atoms/Button";
import useIsMobile from "../../hooks/useIsMobile";
import { extractHiddenBlock } from "../../utils/styles";

interface CustomEmblaCarouselProps {
  slides: ReactNode[];
  options: EmblaOptionsType;
  enableAutoplay?: boolean;
  autoPlayInterval?: number;
  setSelectedIndex?: Dispatch<SetStateAction<number | undefined>>;
  componentName?: string;
}

const CustomEmblaCarousel = dynamic(
  () => import("../../molecules/Embla/EmblaCarousel"),
) as ComponentType<CustomEmblaCarouselProps>;

const buildEmblaOptions = (slidesLength: number): EmblaOptionsType => ({
  loop: true,
  active: slidesLength > 1,
  skipSnaps: false,
  dragFree: false,
});

const parseAutoplayInterval = (value?: string): number =>
  value ? Number(value) : 0;

interface HomepageSlideProps {
  item: HeroCarouselSlide;
  index: number;
  isFirst: boolean;
  isActive: boolean;
}

// Memoised so re-renders driven by `selectedIndex` only update the affected
// slide (active/inactive state) instead of every slide in the list.
const CarouselSlide = memo<HomepageSlideProps>(
  ({ item, index, isFirst, isActive }) => {
    return (
      <div
        className="w-full lg:min-h-[600px] relative"
        data-testid="hero-slide"
        data-evg-component={`hero-carousel-slide-${index + 1}`}
      >
        <NextImage
          field={item?.BackgroundImage?.jsonValue}
          className="w-full h-[240px] lg:h-full object-cover absolute mx-auto right-0 left-auto top-0"
          imageParams={{ mw: 1200, q: 90 }}
          preload={isFirst}
          loading={isFirst ? "eager" : "lazy"}
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 100vw, 1200px"
        />

        <div className="h-full pt-60 lg:pt-0">
          <div className="bg-no-repeat bg-bottom-right bg-bottom-left bg-cover bg-[url('/Mask.svg'),_linear-gradient(228.35deg,_#006bd3_31.38%,_#093675_100%)] text-white setHeight pt-[25px] px-6 pb-24 w-full min-h-[600px] md:min-h-[500px] lg:[clip-path:polygon(0%_0%,_52%_0,_100%_71%,_76%_100%,_0%_100%)] lg:w-[800px] lg:min-h-[600px] lg:pt-8 lg:pr-0 lg:pb-0">
            <Text
              tag={isFirst ? "h1" : "h2"}
              className="lg:w-[50%] text-[34px]! !leading-[1.2] mb-5 mt-0 tracking-[0.4px] lg:tracking-[0.8px]"
              field={item?.Title?.jsonValue}
            />

            <div className="mb-8">
              <Text
                tag="p"
                className="lg:w-[65%] tracking-[0.4px] lg:tracking-[0.8px] text-base line-clamp-4"
                field={item?.Description?.jsonValue}
              />
            </div>

            <div className="flex flex-col gap-5 items-start lg:max-w-4/5">
              <Button
                buttonType={
                  item?.PrimaryCTA?.jsonValue?.value?.class || "primary-reverse"
                }
                className="tracking-[0.4px]"
                field={item?.PrimaryCTA?.jsonValue}
                iconName={
                  item.PrimaryCTA.jsonValue.value.class
                    ? "CHEVRON_BLUE_RIGHT"
                    : ""
                }
                tabIndex={isActive ? 0 : -1}
              />
              <Button
                buttonType={
                  item?.SecondaryCTA?.jsonValue?.value?.class ||
                  "secondary-reverse"
                }
                className="tracking-[0.4px]"
                field={item?.SecondaryCTA?.jsonValue}
                iconName={
                  item?.SecondaryCTA?.jsonValue?.value?.class
                    ? "CHEVRON_BLUE_RIGHT"
                    : ""
                }
                tabIndex={isActive ? 0 : -1}
              />
            </div>
          </div>
        </div>
      </div>
    );
  },
);
CarouselSlide.displayName = "CarouselSlide";

export const HeroCarouselHomepage = memo<HeroCarouselProps>((props) => {
  console.log("---->[HeroCarousel] Component", props.fields);

  const { fields, params } = props;

  const [isClient, setIsClient] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number>();

  const id = params?.RenderingIdentifier ?? undefined;
  const stylesParam = params?.styles;
  const hiddenBlockStyles = extractHiddenBlock(params?.GridParameters);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const slides = useMemo(() => {
    const slidesData = fields?.data?.datasource?.slides?.slidesResult;
    if (!slidesData?.length) return [];

    return slidesData.map((item, index) => (
      <CarouselSlide
        key={`HeroHomepage-${item?.Title?.jsonValue?.value}`}
        item={item}
        index={index}
        isFirst={index === 0}
        isActive={selectedIndex === index}
      />
    ));
  }, [fields?.data?.datasource?.slides?.slidesResult, selectedIndex]);

  const options = useMemo(
    () => buildEmblaOptions(slides.length),
    [slides.length],
  );

  const autoPlayInterval = useMemo(
    () => parseAutoplayInterval(params?.AutoplayInterval),
    [params?.AutoplayInterval],
  );

  if (!slides.length) return null;

  const hasMultipleSlides = slides.length > 1;
  const firstSlide = slides[0];

  return (
    <div
      className={`${stylesParam ?? ""} herocarousel-component mt-[10px] mx-[8px] wrap-anywhere ${hiddenBlockStyles}`}
      id={id}
      data-role="hero-carousel-component"
    >
      <div data-evg-component="hero-carousel" data-testid="hero-carousel">
        {hasMultipleSlides && isClient ? (
          <CustomEmblaCarousel
            slides={slides}
            options={options}
            enableAutoplay={params?.DisableAutoplay !== "1"}
            autoPlayInterval={autoPlayInterval}
            setSelectedIndex={setSelectedIndex}
          />
        ) : (
          <section className="md:w-full sm:w-160 xs:w-full">
            <div className="overflow-hidden">
              <div className="flex">
                <div className="grow-0 shrink-0 basis-full min-w-0">
                  {firstSlide}
                </div>
              </div>
            </div>
          </section>
        )}
      </div>
    </div>
  );
});
HeroCarouselHomepage.displayName = "HeroCarouselHomepage";

export const DefaultHeroCarouselLeft = memo<HeroCarouselProps>((props) => {
  console.log("---->[HeroCarousel] Component", props.fields);

  const { page, fields, params } = props;
  const isPageEditing = page?.mode?.isEditing;

  const [isClient, setIsClient] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number>();

  const id = params?.RenderingIdentifier ?? undefined;
  const hiddenBlockStyles = extractHiddenBlock(params?.GridParameters);
  const isMobile = useIsMobile();

  useEffect(() => {
    setIsClient(true);
  }, []);

  const slides = useMemo(() => {
    const slidesData = fields?.data?.datasource?.slides?.slidesResult;
    if (!slidesData?.length) return [];

    return slidesData.map((slide, index) => (
      <div
        key={`DefaultHeroLeft-${slide?.Title?.jsonValue?.value}`}
        className={`flex relative flex-col-reverse lg:flex-row h-full ${
          !isPageEditing && slide?.BackgroundImage?.jsonValue?.value?.src
            ? `max-lg:bg-none! lg:bg-center lg:bg-no-repeat lg:bg-cover`
            : ""
        }`}
        style={
          !isPageEditing && slide?.BackgroundImage?.jsonValue?.value?.src
            ? {
                backgroundImage: `url(${slide.BackgroundImage.jsonValue.value.src})`,
              }
            : {}
        }
        data-testid="hero-slide"
        data-role="hero-carousel-component"
      >
        <div
          className="bg-ecolab-blue text-white w-full h-full min-h-[400px] lg:flex-none lg:w-[35.01%] lg:min-h-[600px] lg:h-auto flex flex-col items-start px-5 lg:px-8 pt-[50px] lg:pt-[90px] pb-[92px] lg:pb-[120px] leading-[20px]  lg:relative lg:z-[1]"
          data-testid="hero-content"
        >
          {(isPageEditing || slide?.Title?.jsonValue?.value) && (
            <Text
              tag="h1"
              field={slide?.Title?.jsonValue}
              className="text-spacing text-[34px] leading-[1.2] font-medium mb-5 tracking-[0.4px] lg:tracking-[0.8px] lg:mt-0 lg:p-[auto]"
              data-testid="hero-title"
            />
          )}
          {(isPageEditing || slide?.Description?.jsonValue?.value) && (
            <Text
              tag="p"
              field={slide?.Description?.jsonValue}
              className={`text-spacing text-base font-normal tracking-[0.4px] lg:tracking-[0.8px] mb-5 lg:mb-8`}
              data-testid="hero-description"
            />
          )}

          <div className="flex flex-wrap gap-5 items-start max-w-full">
            {(isPageEditing || slide?.PrimaryCTA?.jsonValue?.value?.text) && (
              <Button
                buttonType={
                  slide?.PrimaryCTA?.jsonValue?.value?.class ||
                  "primary-reverse"
                }
                className="tracking-[0.4px] justify-self-start"
                field={slide?.PrimaryCTA?.jsonValue}
                iconName={
                  slide?.PrimaryCTA?.jsonValue?.value?.class
                    ? "CHEVRON_BLUE_RIGHT"
                    : ""
                }
                tabIndex={selectedIndex === index ? 0 : -1}
                data-testid="hero-cta"
                data-role="link"
              />
            )}
            {(isPageEditing || slide?.SecondaryCTA?.jsonValue?.value?.text) && (
              <Button
                buttonType={
                  slide?.SecondaryCTA?.jsonValue?.value?.class ||
                  "secondary-reverse"
                }
                className="tracking-[0.4px] justify-self-start"
                field={slide?.SecondaryCTA?.jsonValue}
                iconName={
                  slide?.SecondaryCTA?.jsonValue?.value?.class
                    ? "CHEVRON_BLUE_RIGHT"
                    : ""
                }
                tabIndex={selectedIndex === index ? 0 : -1}
                data-testid="hero-cta-2"
                data-role="link"
              />
            )}
          </div>
        </div>

        {(isMobile || isPageEditing) && (
          <div
            className={`relative w-full h-auto min-h-[280px] lg:h-auto lg:min-h-[600px] overflow-hidden ${
              isPageEditing && !slide?.BackgroundImage?.jsonValue?.value?.src
                ? "[&_img]:ml-auto"
                : ""
            }`}
            data-testid="hero-image-wrapper"
          >
            <NextImage
              field={slide?.BackgroundImage?.jsonValue}
              className="block w-full h-full min-h-[280px] object-cover lg:relative lg:right-0 lg:left-auto lg:top-0"
              imageParams={{ mw: 1200, q: 90 }}
              data-testid="hero-image"
            />
          </div>
        )}
      </div>
    ));
  }, [
    isPageEditing,
    fields?.data?.datasource?.slides?.slidesResult,
    selectedIndex,
    isMobile,
  ]);

  const options = useMemo(
    () => buildEmblaOptions(slides.length),
    [slides.length],
  );

  const autoPlayInterval = useMemo(
    () => parseAutoplayInterval(params?.AutoplayInterval),
    [params?.AutoplayInterval],
  );

  if (!slides.length) return null;

  const hasMultipleSlides = slides.length > 1;

  return (
    <div
      id={id}
      data-testid="hero-carousel"
      className={`relative w-full overflow-hidden wrap-anywhere ${hiddenBlockStyles}`}
    >
      {hasMultipleSlides && isClient ? (
        <CustomEmblaCarousel
          slides={slides}
          options={options}
          enableAutoplay={params?.DisableAutoplay !== "1"}
          autoPlayInterval={autoPlayInterval}
          setSelectedIndex={setSelectedIndex}
          componentName="DefaultHeroCarouselLeft"
        />
      ) : (
        <section className="md:w-full sm:w-160 xs:w-full">
          <div className="overflow-hidden">
            <div className="flex">
              <div className="grow-0 shrink-0 basis-full min-w-0">
                {slides[0]}
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
});
DefaultHeroCarouselLeft.displayName = "DefaultHeroCarouselLeft";

export const HeroCarouselLeftPattern = memo<HeroCarouselProps>((props) => {
  console.log("---->[HeroCarousel] Component", props.fields);

  const { page, fields, params } = props;
  const isPageEditing = page?.mode?.isEditing;

  const [isClient, setIsClient] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number>();

  const id = params?.RenderingIdentifier ?? undefined;
  const hiddenBlockStyles = extractHiddenBlock(params?.GridParameters);
  const isMobile = useIsMobile();
  const leftPatternRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsClient(true);
  }, []);

  // Equalises slide heights on mobile after layout settles. Embla doesn't
  // do this for us when slides have variable content.
  useEffect(() => {
    if (!isMobile || isPageEditing || !leftPatternRef.current) return;

    const equalizeHeights = () => {
      const slides = leftPatternRef.current?.querySelectorAll<HTMLElement>(
        ".hero-leftPattern-slide",
      );
      if (!slides) return;

      let maxHeight = 400;
      slides.forEach((slide) => {
        if (slide.offsetHeight > maxHeight) maxHeight = slide.offsetHeight;
      });
      slides.forEach((slide) => {
        slide.style.minHeight = `${maxHeight}px`;
      });
    };

    const timer = setTimeout(equalizeHeights, 500);
    return () => clearTimeout(timer);
  }, [isMobile, isPageEditing]);

  const slides = useMemo(() => {
    const slidesData = fields?.data?.datasource?.slides?.slidesResult;
    if (!slidesData?.length) return [];

    return slidesData.map((slide, index) => (
      <div
        key={`HeroLeftPattern-${slide?.Title?.jsonValue?.value}`}
        data-testid="hero-slide"
        className={`flex flex-col-reverse lg:flex-row w-full lg:min-h-[600px] lg:h-full! ${
          isPageEditing ? "full-height" : ""
        }  ${
          !isPageEditing && slide?.BackgroundImage?.jsonValue?.value?.src
            ? `max-lg:bg-none! lg:bg-center lg:bg-no-repeat lg:bg-cover`
            : ""
        }`}
        style={
          !isPageEditing &&
          !isMobile &&
          slide?.BackgroundImage?.jsonValue?.value?.src
            ? {
                backgroundImage: `url(${slide.BackgroundImage.jsonValue.value.src})`,
              }
            : {}
        }
        data-role="hero-carousel-component"
      >
        <div
          data-testid="hero-content"
          className="hero-leftPattern-slide h-[stretch] hero-bg-pattern text-white px-8 pt-10 pb-[92px] lg:pt-10 w-full min-h-[400px] h-full lg:w-[35.01%] lg:min-h-[600px] lg:relative lg:z-[1] lg:pb-[120px]"
        >
          {(isPageEditing || slide?.Title?.jsonValue?.value) && (
            <Text
              tag="h1"
              field={slide?.Title?.jsonValue}
              className="w-auto text-[34px] font-medium leading-[1.2] tracking-[0.4px] lg:tracking-[0.8px] mt-0 mb-5"
              data-testid="hero-title"
            />
          )}

          {(isPageEditing || slide?.Description?.jsonValue?.value) && (
            <Text
              tag="p"
              field={slide?.Description?.jsonValue}
              className="text-base tracking-[0.4px] lg:tracking-[0.8px] mb-8"
              data-testid="hero-description"
            />
          )}

          <div className="flex flex-wrap gap-5 items-start max-w-full">
            {(isPageEditing || slide?.PrimaryCTA?.jsonValue?.value?.text) && (
              <Button
                buttonType={
                  slide?.PrimaryCTA?.jsonValue?.value?.class ||
                  "primary-reverse"
                }
                className="tracking-[0.4px] justify-self-start"
                field={slide?.PrimaryCTA?.jsonValue}
                iconName={
                  slide?.PrimaryCTA?.jsonValue?.value?.class
                    ? "CHEVRON_BLUE_RIGHT"
                    : ""
                }
                tabIndex={selectedIndex === index ? 0 : -1}
                data-testid="hero-cta"
                data-role="link"
              />
            )}
            {(isPageEditing || slide?.SecondaryCTA?.jsonValue?.value?.text) && (
              <Button
                buttonType={
                  slide?.SecondaryCTA?.jsonValue?.value?.class ||
                  "secondary-reverse"
                }
                className="tracking-[0.4px] justify-self-start"
                field={slide?.SecondaryCTA?.jsonValue}
                iconName={
                  slide?.SecondaryCTA?.jsonValue?.value?.class
                    ? "CHEVRON_BLUE_RIGHT"
                    : ""
                }
                tabIndex={selectedIndex === index ? 0 : -1}
                data-testid="hero-cta-2"
                data-role="link"
              />
            )}
          </div>
        </div>

        <div
          className={`relative flex-1 w-full h-[280px] lg:h-auto lg:min-h-[600px] overflow-hidden ${
            isPageEditing && !slide?.BackgroundImage?.jsonValue?.value?.src
              ? "[&_img]:ml-auto"
              : ""
          }`}
          data-testid="hero-image-wrapper"
        >
          <NextImage
            field={slide?.BackgroundImage?.jsonValue}
            className="block w-full h-full min-h-[280px] object-cover"
            imageParams={{ mw: 1600, q: 90 }}
            data-testid="hero-image"
          />
        </div>
      </div>
    ));
  }, [
    isPageEditing,
    isMobile,
    fields?.data?.datasource?.slides?.slidesResult,
    selectedIndex,
  ]);

  const options = useMemo(
    () => buildEmblaOptions(slides.length),
    [slides.length],
  );

  const autoPlayInterval = useMemo(
    () => parseAutoplayInterval(params?.AutoplayInterval),
    [params?.AutoplayInterval],
  );

  if (!slides.length) return null;

  const hasMultipleSlides = slides.length > 1;

  return (
    <div
      id={id}
      ref={leftPatternRef}
      data-testid="hero-carousel"
      className={`relative w-full overflow-hidden wrap-anywhere ${hiddenBlockStyles}`}
    >
      {hasMultipleSlides && isClient ? (
        <CustomEmblaCarousel
          slides={slides}
          options={options}
          enableAutoplay={params?.DisableAutoplay !== "1"}
          autoPlayInterval={autoPlayInterval}
          setSelectedIndex={setSelectedIndex}
          componentName="HeroCarouselLeftPattern"
        />
      ) : (
        <section className="w-full">
          <div className="overflow-hidden">
            <div className="flex">
              <div className="grow-0 shrink-0 basis-full min-w-0">
                {slides[0]}
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
});
HeroCarouselLeftPattern.displayName = "HeroCarouselLeftPattern";

export const HeroCarouselNoBox = memo<HeroCarouselProps>((props) => {
  console.log("---->[HeroCarousel] Component", props.fields);

  const { page, fields, params } = props;
  const isPageEditing = page?.mode?.isEditing;

  const [isClient, setIsClient] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number>();

  const id = params?.RenderingIdentifier ?? undefined;
  const hiddenBlockStyles = extractHiddenBlock(params?.GridParameters);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const slides = useMemo(() => {
    const slidesData = fields?.data?.datasource?.slides?.slidesResult;
    const isMcClarenImageOnlyCarousel = params?.Styles?.includes(
      "mcclaren-image-only",
    );
    if (!slidesData?.length) return [];

    return slidesData.map((slide, index) => (
      <div
        key={`HeroNoBox-${slide?.Title?.jsonValue?.value}`}
        data-testid="hero-slide"
        className={`relative w-full h-auto ${
          slide?.Title?.jsonValue?.value || slide?.Description?.jsonValue?.value
            ? "min-h-[300px]"
            : "min-h-[160px]"
        } lg:min-h-[600px] lg:h-full flex flex-col text-white`}
        data-role="hero-carousel-component"
      >
        <NextImage
          field={slide?.BackgroundImage?.jsonValue}
          className={`w-full object-cover  ${
            isMcClarenImageOnlyCarousel
              ? "h-auto"
              : "h-[25vh] lg:h-full lg:absolute lg:right-0 lg:left-auto lg:top-0 lg:inset-0"
          }`}
          imageParams={{ mw: 1600 }}
          data-testid="hero-image"
        />

        {(isPageEditing ||
          slide?.Title?.jsonValue?.value ||
          slide?.Description?.jsonValue?.value ||
          slide?.PrimaryCTA?.jsonValue?.value?.text ||
          slide?.SecondaryCTA?.jsonValue?.value?.text) && (
          <div
            className={`relative z-10 w-full flex flex-col px-6 max-lg:pt-8 max-lg:pb-24 lg:py-0 lg:h-[100%] lg:pl-[90px] mx-auto ${
              isPageEditing
                ? "lg:max-w-[calc(43.6%+180px)] lg:bg-[rgba(0,0,0,0.3)] lg:border-dashed lg:border-1 lg:ml-0 lg:pr-[90px]"
                : "lg:px-[160px]"
            } ${isMcClarenImageOnlyCarousel ? "hidden" : ""}`}
          >
            <div
              className={`w-full ${
                isPageEditing ? "" : "lg:w-[43.6%]"
              } lg:h-[100%] lg:py-[120px] lg:px-8 lg:min-h-[600px]`}
            >
              {(slide?.Title?.jsonValue || isPageEditing) && (
                <Text
                  tag="h1"
                  field={slide?.Title?.jsonValue}
                  className="text-[48px] font-[700] tracking-[0.4px] leading-[57.6px] mb-4 lg:text-[54px] lg:tracking-[0.8px] lg:leading-[64.8px] lg:w-[70%] text-shadow-[.0em_.0em_0.4em_#000000,_.0em_.0em_0.4em_#000000]"
                  data-testid="hero-title"
                />
              )}

              {(slide?.Description?.jsonValue?.value || isPageEditing) && (
                <Text
                  tag="p"
                  field={slide?.Description?.jsonValue}
                  className="text-[18px] font-[700] tracking-[0.4px] leading-[27px] mb-8 lg:tracking-[0.8px] lg:w-[70%] text-shadow-[.0em_.0em_0.4em_#000000,_.0em_.0em_0.4em_#000000]"
                  data-testid="hero-description"
                />
              )}

              <div className="flex flex-wrap gap-4 justify-start">
                {(slide?.PrimaryCTA?.jsonValue?.value?.text ||
                  isPageEditing) && (
                  <Button
                    buttonType={
                      slide?.PrimaryCTA?.jsonValue?.value?.class ||
                      "primary-reverse"
                    }
                    field={slide?.PrimaryCTA?.jsonValue}
                    iconName={
                      slide?.PrimaryCTA?.jsonValue?.value?.class
                        ? "CHEVRON_BLUE_RIGHT"
                        : ""
                    }
                    tabIndex={selectedIndex === index ? 0 : -1}
                    data-testid="hero-cta-primary"
                    data-role="link"
                  />
                )}
                {(slide?.SecondaryCTA?.jsonValue?.value?.text ||
                  isPageEditing) && (
                  <Button
                    buttonType={
                      slide?.SecondaryCTA?.jsonValue?.value?.class ||
                      "secondary-reverse"
                    }
                    field={slide?.SecondaryCTA?.jsonValue}
                    iconName={
                      slide?.SecondaryCTA?.jsonValue?.value?.class
                        ? "CHEVRON_BLUE_RIGHT"
                        : ""
                    }
                    tabIndex={selectedIndex === index ? 0 : -1}
                    data-testid="hero-cta-secondary"
                    data-role="link"
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    ));
  }, [
    fields?.data?.datasource?.slides?.slidesResult,
    selectedIndex,
    isPageEditing,
    params?.Styles,
  ]);

  const options = useMemo(
    () => buildEmblaOptions(slides.length),
    [slides.length],
  );

  const autoPlayInterval = useMemo(
    () => parseAutoplayInterval(params?.AutoplayInterval),
    [params?.AutoplayInterval],
  );

  if (!slides.length) return null;

  const hasMultipleSlides = slides.length > 1;

  return (
    <div
      id={id}
      className={`relative w-full mb-[var(--spacing-thirtytwo)] pt-[20px] wrap-anywhere ${hiddenBlockStyles}`}
    >
      <div>
        {hasMultipleSlides && isClient ? (
          <CustomEmblaCarousel
            slides={slides}
            options={options}
            enableAutoplay={params?.DisableAutoplay !== "1"}
            autoPlayInterval={autoPlayInterval}
            setSelectedIndex={setSelectedIndex}
            componentName="HeroCarouselNoBox"
          />
        ) : (
          <section className="w-full">
            <div className="overflow-hidden">
              <div className="flex">
                <div className="grow-0 shrink-0 basis-full min-w-0">
                  {slides[0]}
                </div>
              </div>
            </div>
          </section>
        )}
      </div>
    </div>
  );
});
HeroCarouselNoBox.displayName = "HeroCarouselNoBox";

export const HeroCarouselMcClaren = memo<HeroCarouselProps>((props) => {
  console.log("---->[HeroCarousel] Component", props.fields);

  const { page, fields, params } = props;
  const isPageEditing = page?.mode?.isEditing;

  const [isClient, setIsClient] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number>();

  const id = params?.RenderingIdentifier ?? undefined;
  const hiddenBlockStyles = extractHiddenBlock(params?.GridParameters);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const slides = useMemo(() => {
    const slidesData = fields?.data?.datasource?.slides?.slidesResult;
    if (!slidesData?.length) return [];

    const hasMultiple = slidesData.length > 1;

    return slidesData.map((slide, index) => {
      const ctaTabIndex = hasMultiple && selectedIndex !== index ? -1 : 0;

      return (
        <div
          key={`HeroMcClaren-${slide?.Title?.jsonValue?.value}`}
          className={`flex flex-col-reverse lg:flex-row h-full gap-x-8 ${
            hasMultiple ? "max-lg: pb-[90px]" : ""
          }`}
          data-testid="hero-slide"
          data-role="hero-carousel-component"
        >
          {(isPageEditing || slide?.BackgroundImage?.jsonValue) && (
            <div
              className="relative max-lg:hidden w-full flex lg:items-center h-auto bg-transparent lg:max-w-[calc(50%-16px)] lg:w-1/2 lg:min-h-[510px] lg:h-full overflow-hidden lg:pt-[30px] lg:pb-5"
              data-testid="hero-image-wrapper"
            >
              <NextImage
                field={slide?.BackgroundImage?.jsonValue}
                className=" lg:w-auto lg:h-auto max-h-full max-w-full lg:object-[77%_center]"
                imageParams={{ mw: 800 }}
                data-testid="hero-image"
              />
            </div>
          )}
          <div
            className="text-white w-full h-full min-h-[400px] wrap-anywhere lg:flex-none lg:max-w-[calc(50%-16px)] lg:min-h-[510px] flex flex-col lg:items-center items-start lg:justify-center max-lg:px-5 pt-[50px] lg:pt-[60px]"
            data-testid="hero-content"
          >
            {(isPageEditing || slide?.Title?.jsonValue) && (
              <Text
                tag="h2"
                field={slide?.Title?.jsonValue}
                className="font-['Special_Gothic_Condensed_One','Roboto'] text-[56px] [text-shadow:0_0_.7em_#fff] lg:text-[96px] leading-[0.8] font-medium mb-5 tracking-[0.4px] lg:tracking-[0.8px] lg:mt-0 max-lg:mt-0"
                data-testid="hero-title"
              />
            )}
            {(isPageEditing || slide?.BackgroundImage?.jsonValue) && (
              <div className="mb-5 w-full lg:hidden">
                <NextImage
                  field={slide?.BackgroundImage?.jsonValue}
                  className="h-auto w-auto max-w-full max-h-full object-cover "
                  imageParams={{ mw: 600 }}
                  data-testid="hero-image"
                />
              </div>
            )}
            {(isPageEditing || slide?.Description?.jsonValue?.value) && (
              <Text
                tag="p"
                field={slide?.Description?.jsonValue}
                className="text-[20px] leading-[1.5] tracking-[0.4px] mb-5 lg:tracking-[0.8px]"
                data-testid="hero-description"
              />
            )}

            {(isPageEditing ||
              slide?.PrimaryCTA?.jsonValue?.value?.text ||
              slide?.SecondaryCTA?.jsonValue?.value?.text) && (
              <div className="flex flex-wrap gap-y-4 self-start mb-1 max-w-full">
                {(isPageEditing ||
                  slide?.PrimaryCTA?.jsonValue?.value?.text) && (
                  <Button
                    buttonType={
                      slide?.PrimaryCTA?.jsonValue?.value?.class ||
                      "primary-reverse"
                    }
                    className="mr-[10px]"
                    field={slide?.PrimaryCTA?.jsonValue}
                    iconName={
                      slide?.PrimaryCTA?.jsonValue?.value?.class
                        ? "CHEVRON_BLUE_RIGHT"
                        : ""
                    }
                    tabIndex={ctaTabIndex}
                    data-testid="hero-cta"
                    data-role="link"
                  />
                )}
                {(isPageEditing ||
                  slide?.SecondaryCTA?.jsonValue?.value?.text) && (
                  <Button
                    buttonType={
                      slide?.SecondaryCTA?.jsonValue?.value?.class ||
                      "secondary-reverse"
                    }
                    className="mr-[10px]"
                    field={slide?.SecondaryCTA?.jsonValue}
                    iconName={
                      slide?.SecondaryCTA?.jsonValue?.value?.class
                        ? "CHEVRON_BLUE_RIGHT"
                        : ""
                    }
                    tabIndex={ctaTabIndex}
                    data-testid="hero-cta"
                    data-role="link"
                  />
                )}
              </div>
            )}
          </div>
        </div>
      );
    });
  }, [
    fields?.data?.datasource?.slides?.slidesResult,
    selectedIndex,
    isPageEditing,
  ]);

  const options = useMemo(
    () => buildEmblaOptions(slides.length),
    [slides.length],
  );

  const autoPlayInterval = useMemo(
    () => parseAutoplayInterval(params?.AutoplayInterval),
    [params?.AutoplayInterval],
  );

  if (!slides.length) return null;

  const hasMultipleSlides = slides.length > 1;

  return (
    <div
      id={id}
      data-testid="hero-carousel"
      className={`relative w-full overflow-hidden pt-[20px] lg:px-4 wrap-anywhere ${hiddenBlockStyles}`}
    >
      {hasMultipleSlides && isClient ? (
        <CustomEmblaCarousel
          slides={slides}
          options={options}
          enableAutoplay={params?.DisableAutoplay !== "1"}
          autoPlayInterval={autoPlayInterval}
          setSelectedIndex={setSelectedIndex}
          componentName="HeroCarouselMcClarenVariant"
        />
      ) : (
        <section className="md:w-full sm:w-160 xs:w-full">
          <div className="overflow-hidden">
            <div className="flex">
              <div className="grow-0 shrink-0 basis-full min-w-0">
                {slides[0]}
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
});
HeroCarouselMcClaren.displayName = "HeroCarouselMcClaren";

// Default variant alias for the new-src convention.
export const Default = DefaultHeroCarouselLeft;
